import gql from 'graphql-tag'
import qs from 'query-string'
import * as React from 'react'
import { Query } from 'react-apollo'
import { RouteComponentProps } from 'react-router'
import styled from 'styled-components'
import Alert from 'web-makeup/dist/Alert'
import Box from 'web-makeup/dist/Box'
import Select from 'web-makeup/dist/Select'

import { GetAllProducers, ProducerProfile } from '../../typings/types'
import TableList, { columnType } from '../TableList'
import {
  GET_ALL_SERVICE_QUALITY_LEVELS,
  AllServiceQualityLevelsQuery,
} from '../ServiceQualityLevels'

import CountrySelect from './../CountrySelect'

const { Option } = Select

const Heading = styled.header`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  align-items: center;
`

const Filters = styled.ul`
  display: flex;
  align-items: center;
  list-style-type: none;

  > li {
    margin-left: 1rem;
  }
`

const StyledBox = styled(Box)`
  max-width: 100%;
  padding: 2rem;
`

const StyledSelect = styled(Select as any)`
  /* without this the placeholder has no width dimension */
  width: 8rem;
`

const Title = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #4a4a4a;
`

const GET_ALL_PRODUCERS = gql`
  query GetAllProducers(
    $producerFilter: ProducerFilterInput
    $locationFilter: LocationFilterInput
    $page: Int
    $pageSize: Int
  ) {
    allProducers(
      producerFilter: $producerFilter
      locationFilter: $locationFilter
      page: $page
      pageSize: $pageSize
    ) {
      pageInfo {
        currentPage
        lastPage
        totalCount
      }
      producers {
        activated
        online
        createdAt
        serviceQualityLevel {
          name
        }
        user {
          avatar {
            cloudinaryId
            cloudinaryType
          }
          name
          email
          phoneNumber
          skypeUsername
          location {
            city
            country
            countryCode
          }
          createdAt
          uuid
        }
      }
    }
  }
`

const columns = [
  {
    columns: [
      {
        columnName: 'online',
        columnType: columnType.STATUS,
      },
    ],
    visible: true,
  },
  {
    name: 'User',
    columns: [
      {
        columnName: 'user.avatar',
        columnType: columnType.IMAGE,
      },
    ],
    visible: true,
  },
  {
    columns: [
      {
        columnName: 'user.name',
        columnType: columnType.TEXT,
      },
    ],
    visible: true,
  },
  {
    name: 'Email',
    columns: [
      {
        columnName: 'user.email',
        columnType: columnType.TEXT,
      },
    ],
    visible: true,
  },
  {
    name: 'Phone Number',
    columns: [
      {
        columnName: 'user.phoneNumber',
        columnType: columnType.TEXT,
      },
    ],
    visible: true,
  },
  {
    name: 'Skype Info',
    columns: [
      {
        columnName: 'user.skypeUsername',
        columnType: columnType.TEXT,
      },
    ],
    visible: true,
  },
  {
    name: 'Location',
    columns: [
      {
        columnName: 'user.location.city',
        columnType: columnType.TEXT,
      },
    ],
    visible: true,
  },
  {
    name: 'Service Level',
    columns: [
      {
        columnName: 'serviceQualityLevel.name',
        columnType: columnType.TEXT,
      },
    ],
    visible: true,
  },
  {
    name: 'Signed Up',
    columns: [
      {
        columnName: 'createdAt',
        columnType: columnType.DATE,
      },
    ],
    visible: true,
  },
]

const ITEMS_PER_PAGE = 20

class AllProducersQuery extends Query<GetAllProducers.Query, GetAllProducers.Variables> {}

type Props = RouteComponentProps<{}>

type FilterQueryStringsProps = Partial<{
  status: boolean
  blocked: boolean
  activated: boolean
  countryCode: string
  serviceLevel: string
}>

export class Producers extends React.Component<Props, {}> {
  handleStatusFilter: (val) => void
  handleCountryFilter: (countryCode: string) => void
  handleServiceLevelFilter: (serviceLevel: string) => void

  constructor(props: Props) {
    super(props)

    this.onPageChange = this.onPageChange.bind(this)
    this.onRowClick = this.onRowClick.bind(this)

    this.handleStatusFilter = this.handleFilterChange('status')
    this.handleCountryFilter = this.handleFilterChange('countryCode')
    this.handleServiceLevelFilter = this.handleFilterChange('serviceLevel')
  }

  onPageChange(nextPage: number) {
    const params = {
      ...qs.parse(this.props.location.search),
      page: nextPage,
    }
    this.props.history.push(`/producers?${qs.stringify(params)}`)
  }

  onRowClick(row: ProducerProfile) {
    this.props.history.push(`/producers/${row.user.uuid}`)
  }

  handleFilterChange(filterKey: keyof FilterQueryStringsProps) {
    return filterValue => {
      const { history, match, location } = this.props

      const prevFilters = qs.parse(location.search)
      const nextFilter: FilterQueryStringsProps = { [filterKey]: filterValue }

      const params = {
        ...prevFilters,
        page: undefined, // reset page count everytime filter is applied
        ...nextFilter,
      }

      history.push(`${match.url}?${qs.stringify(params)}`)
    }
  }

  render() {
    const params = qs.parse(this.props.location.search)

    const queryVariables = {
      producerFilter: {
        activated: params.activated && JSON.parse(params.activated),
        status: params.status && params.status,
        blocked: params.blocked && JSON.parse(params.blocked),
        serviceQualityLevelSlugs: params.serviceLevel && [params.serviceLevel],
      },
      locationFilter: {
        countryCode: params.countryCode,
      },
      page: params.page && parseInt(params.page, 10),
      pageSize: params.pageSize ? parseInt(params.pageSize, 10) : ITEMS_PER_PAGE,
    }

    return (
      <AllProducersQuery query={GET_ALL_PRODUCERS} variables={queryVariables} pollInterval={10000}>
        {({ loading, error, data }) => {
          if (error) {
            return error.graphQLErrors.map((e, i) => (
              <Alert message="Error" description={e.message} key={i} type="error" />
            ))
          }
          return (
            <StyledBox>
              <Heading>
                <Title>Accounts</Title>
                <Filters>
                  <li>
                    <AllServiceQualityLevelsQuery query={GET_ALL_SERVICE_QUALITY_LEVELS}>
                      {({ loading: serviceLevelLoading, data: serviceLevels }) => (
                        <StyledSelect
                          className="qa-service-select"
                          allowClear
                          value={params.serviceLevel}
                          placeholder="Service Level"
                          onChange={this.handleServiceLevelFilter}
                        >
                          {serviceLevels &&
                            serviceLevels.allServiceQualityLevels &&
                            serviceLevels.allServiceQualityLevels.map(l => (
                              <Option key={l.slug} value={l.slug}>
                                {l.name}
                              </Option>
                            ))}
                        </StyledSelect>
                      )}
                    </AllServiceQualityLevelsQuery>
                  </li>
                  <li>
                    <CountrySelect
                      className="qa-country-select"
                      value={params.countryCode}
                      onChange={this.handleCountryFilter}
                    />
                  </li>
                  <li>
                    <StyledSelect
                      className="qa-availability-select"
                      allowClear
                      value={params.status}
                      placeholder="Status"
                      onChange={this.handleStatusFilter}
                    >
                      <Option value="ONLINE">Online</Option>
                      <Option value="OFFLINE">Offline</Option>
                      <Option value="PREFERRED_ONLY">Preferred</Option>
                    </StyledSelect>
                  </li>
                </Filters>
              </Heading>
              <TableList
                data={data ? data.allProducers && data.allProducers.producers : []}
                columns={columns}
                onPageChange={this.onPageChange}
                onRowClick={this.onRowClick}
                loading={loading}
                pagination={{
                  pageSize: queryVariables.pageSize,
                  current: data && data.allProducers && data.allProducers.pageInfo.currentPage,
                  total: data && data.allProducers && data.allProducers.pageInfo.totalCount,
                }}
              />
            </StyledBox>
          )
        }}
      </AllProducersQuery>
    )
  }
}

export default Producers



// WEBPACK FOOTER //
// ./src/components/Producers/Producers.tsx